// Push Notifications and Offline utilities for Lumina

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Check if browser supports notifications
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Check if browser is online
export const isOnline = () => navigator.onLine;

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

// Register service worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Subscribe to push notifications
export const subscribeToPush = async (registration) => {
  if (!registration) {
    registration = await navigator.serviceWorker.ready;
  }

  try {
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription && VAPID_PUBLIC_KEY) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      console.log('Push subscription created:', subscription);
    }
    
    return subscription;
  } catch (error) {
    console.error('Push subscription failed:', error);
    return null;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      console.log('Push subscription removed');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Push unsubscription failed:', error);
    return false;
  }
};

// Show local notification (for reminders)
export const showLocalNotification = async (title, options = {}) => {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    console.warn('Notifications not available');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification(title, {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      tag: 'lumina-local-notification',
      ...options
    });
    
    return true;
  } catch (error) {
    console.error('Failed to show notification:', error);
    return false;
  }
};

// Schedule a reminder notification
export const scheduleReminderNotification = (reminder) => {
  const scheduledTime = new Date(reminder.scheduled_date).getTime();
  const now = Date.now();
  const delay = scheduledTime - now;

  if (delay <= 0) {
    console.log('Reminder time has passed');
    return null;
  }

  // Store in localStorage for persistence
  const pendingReminders = JSON.parse(localStorage.getItem('pendingReminders') || '[]');
  pendingReminders.push({
    id: reminder.id,
    title: 'Rappel Lumina',
    body: reminder.message,
    scheduledTime: scheduledTime,
    data: { url: '/reminders' }
  });
  localStorage.setItem('pendingReminders', JSON.stringify(pendingReminders));

  // Set timeout for notification
  const timeoutId = setTimeout(() => {
    showLocalNotification('Rappel Lumina', {
      body: reminder.message,
      data: { url: '/reminders', reminderId: reminder.id }
    });
    
    // Remove from pending
    const updated = JSON.parse(localStorage.getItem('pendingReminders') || '[]')
      .filter(r => r.id !== reminder.id);
    localStorage.setItem('pendingReminders', JSON.stringify(updated));
  }, delay);

  return timeoutId;
};

// Check and trigger pending reminders on load
export const checkPendingReminders = () => {
  const pendingReminders = JSON.parse(localStorage.getItem('pendingReminders') || '[]');
  const now = Date.now();
  const stillPending = [];

  pendingReminders.forEach(reminder => {
    if (reminder.scheduledTime <= now) {
      // Show missed notification
      showLocalNotification(reminder.title, {
        body: `${reminder.body} (manqué)`,
        data: reminder.data
      });
    } else {
      // Re-schedule
      stillPending.push(reminder);
      setTimeout(() => {
        showLocalNotification(reminder.title, {
          body: reminder.body,
          data: reminder.data
        });
      }, reminder.scheduledTime - now);
    }
  });

  localStorage.setItem('pendingReminders', JSON.stringify(stillPending));
};

// Cache reading for offline access
export const cacheReading = async (reading) => {
  try {
    const readings = JSON.parse(localStorage.getItem('cachedReadings') || '[]');
    const existingIndex = readings.findIndex(r => r.id === reading.id);
    
    if (existingIndex >= 0) {
      readings[existingIndex] = reading;
    } else {
      readings.unshift(reading);
      // Keep only last 20 readings
      if (readings.length > 20) readings.pop();
    }
    
    localStorage.setItem('cachedReadings', JSON.stringify(readings));
    return true;
  } catch (error) {
    console.error('Failed to cache reading:', error);
    return false;
  }
};

// Get cached readings
export const getCachedReadings = () => {
  try {
    return JSON.parse(localStorage.getItem('cachedReadings') || '[]');
  } catch {
    return [];
  }
};

// Cache favorites for offline access
export const cacheFavorites = async (favorites) => {
  try {
    localStorage.setItem('cachedFavorites', JSON.stringify(favorites));
    return true;
  } catch (error) {
    console.error('Failed to cache favorites:', error);
    return false;
  }
};

// Get cached favorites
export const getCachedFavorites = () => {
  try {
    return JSON.parse(localStorage.getItem('cachedFavorites') || '[]');
  } catch {
    return [];
  }
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Network status listener
export const onNetworkStatusChange = (callback) => {
  window.addEventListener('online', () => callback(true));
  window.addEventListener('offline', () => callback(false));
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', () => callback(true));
    window.removeEventListener('offline', () => callback(false));
  };
};
